import { flow, getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import {
  APIService,
  DownloadParams,
  LoginParams,
  RegisterParams,
  UploadParams,
  UploadClientParams,
  UploadToS3StorageParams,
  CheckVersionParams,
  UploadClientResult,
  PingParams,
  ForgetPasswordParams,
  ResetPasswordParams,
  PhoneCodeParams,
  RevisePasswordParams,
  VerifyParams,
  PhoneSmsParams,
  CreatePayPwdParams,
  UpdatePayPwdParams,
  CreatePayParams,
  GetParams,
  DeletePayParams,
  CreateExchangeParams,
  CancelExchangeParams,
  SaveComplaintParams,
  MyCreditParams,
  SaveCreditParams,
  EmailSmsParams,
  UpdateEmailParams,
  CheckPayParams,
} from "../../services/api"
import { RootStoreModel } from ".."

/**
 * 管理API请求
 */
export const APIStoreModel = types
  .model("ApiStore")
  .props({})
  .actions((self) => {
    const { configStore, authStore } = getRoot<Instance<typeof RootStoreModel>>(self)

    // http client
    const api = new APIService(configStore.api)

    // 注册账号
    const register = flow(function* (data: RegisterParams) {
      return yield api.register(data)
    })

    // 登录账号
    const login = flow(function* (data: LoginParams) {
      return yield api.login(data)
    })

     // 手机验证码
    const getPhoneSmsv1 = flow(function* (data: PhoneCodeParams) {
      return yield api.phoneSmsv1(data)
    })


    // 忘记密码
    const forgetPassword = flow(function* (data: ForgetPasswordParams) {
      const params = { ...data }
      return yield api.forgetPassword(params)
    })

    // 重置密码
    const resetPassword = flow(function* (data: ResetPasswordParams) {
      const params = { ...data }
      return yield api.resetPassword(params)
    })

    const revisePassword = flow(function* (data: RevisePasswordParams) {
      return yield api.revisePassword(data)
    })

    // 上传, 状态独立管理，因为可能会存在多任务同时执行的情况
    const upload = flow(function* (data: UploadParams) {
      return yield api.upload(data)
    })

    const uploadClient = flow(function* (data: UploadClientParams) {
      return yield api.uploadClient(data)
    })

    // 登录账号
    const ping = flow(function* (data: PingParams) {
      return yield api.ping(data)
    })

    const uploadToS3Storage = flow(function* (
      s3url: UploadClientResult["url"],
      formData: UploadToS3StorageParams,
      onProgressUpdate: (progress: number) => void,
    ) {
      return yield api.uploadToS3Storage(s3url, formData, onProgressUpdate)
    })

    // 下载
    const download = flow(function* (data: DownloadParams) {
      return yield api.download(data)
    })

    const checkVersion = flow(function* (data: CheckVersionParams) {
      return yield api.checkVersion(data)
    })

    const getWalletInfo = flow(function* (data: GetParams) {
      return yield api.getWalletInfo(data)
    })
    const getWalletLog = flow(function* (data: GetParams) {
      return yield api.getWalletLog(data)
    })
    const getTakeOrders = flow(function* (data: GetParams) {
      return yield api.getTakeOrders(data)
    })
    const getTakeOrdersSold = flow(function* (data: GetParams) {
      return yield api.getTakeOrdersSold(data)
    })
    const getTakeOrdersDisputed = flow(function* (data: GetParams) {
      return yield api.getTakeOrdersDisputed(data)
    })
    const getForceCs = flow(function* (data: GetParams) {
      return yield api.getForceCs(data)
    })
    const getBankList = flow(function* () {
      return yield api.getBankList()
    })

    const getPaymentList = flow(function* (data: GetParams) {
      return yield api.getPaymentList(data)
    })

    const getPendingOrders = flow(function* (data: GetParams) {
      return yield api.getPendingOrders(data)
    })

    const getMyOrders = flow(function* (data: GetParams) {
      return yield api.getMyOrders(data)
    })

    const getMyOrderDetail = flow(function* (data: GetParams) {
      return yield api.getMyOrderDetail(data)
    })

    const deletePayment = flow(function* (data: DeletePayParams) {
      return yield api.deletePayment(data)
    })

    const verify = flow(function* (data: VerifyParams) {
      return yield api.verify(data)
    })

    const phoneVerify = flow(function* (data: PhoneSmsParams) {
      return yield api.phoneVerify(data)
    })

    const getPhoneSms = flow(function* (data: PhoneSmsParams) {
      return yield api.phoneSms(data)
    })

    const paypws = flow(function* (data: CreatePayPwdParams) {
      return yield api.paypws(data)
    })

    const payCheck = flow(function* (data: CheckPayParams) {
      return yield api.payCheck(data)
    })

    const updatePaypws = flow(function* (data: UpdatePayPwdParams) {
      return yield api.updatePaypws(data)
    })

    const createPay = flow(function* (data: CreatePayParams, token: string) {
      return yield api.createPay(data, token)
    })

    const getExchangeRate = flow(function* (data: GetParams) {
      return yield api.getExchangeRate(data)
    })

    const getExchangeList = flow(function* (data: GetParams) {
      return yield api.getExchangeList(data)
    })

    const createExchange = flow(function* (data: CreateExchangeParams) {
      return yield api.createExchange(data)
    })

    const cancelExchange = flow(function* (data: CancelExchangeParams) {
      return yield api.cancelExchange(data)
    })

    const getComplaintList = flow(function* (data: GetParams) {
      return yield api.getComplaintList(data)
    })

    const saveComplaint = flow(function* (data: SaveComplaintParams) {
      return yield api.saveComplaint(data)
    })

    const getRatingRule = flow(function* () {
      const token = authStore.getToken
      return yield api.getRatingRule({ token })
    })

    const getCreditRule = flow(function* () {
      const token = authStore.getToken
      return yield api.getCreditRule({ token })
    })

    const getMyCredit = flow(function* (data: MyCreditParams) {
      const token = authStore.getToken
      return yield api.getMyCredit(data, token)
    })

    const saveCredit = flow(function* (data: SaveCreditParams) {
      const token = authStore.getToken
      return yield api.saveCredit(data, token)
    })

    const getEmailSms = flow(function* (data: EmailSmsParams) {
      const token = authStore.getToken
      return yield api.emailSms(data, token)
    })

    const updateEmail = flow(function* (data: UpdateEmailParams) {
      const token = authStore.getToken
      return yield api.updateEmail(data, token)
    })

    return {
      register,
      login,
      forgetPassword,
      resetPassword,
      revisePassword,
      upload,
      uploadClient,
      ping,
      uploadToS3Storage,
      download,
      checkVersion,
      getPhoneSmsv1,
      getWalletInfo,
      getWalletLog,
      getTakeOrders,
      getTakeOrdersSold,
      getTakeOrdersDisputed,
      getForceCs,
      verify,
      getPhoneSms,
      phoneVerify,
      paypws,
      updatePaypws,
      createPay,
      getPaymentList,
      deletePayment,
      getPendingOrders,
      getMyOrders,
      getMyOrderDetail,
      payCheck,
      getBankList,
      getExchangeRate,
      createExchange,
      getExchangeList,
      cancelExchange,
      getComplaintList,
      saveComplaint,
      getRatingRule,
      getCreditRule,
      getMyCredit,
      saveCredit,
      getEmailSms,
      updateEmail,
    }
  })

type ApiStoreType = Instance<typeof APIStoreModel>
export interface ApiStore extends ApiStoreType {}
type ApiStoreSnapshotType = SnapshotOut<typeof APIStoreModel>
export interface ApiStoreSnapshot extends ApiStoreSnapshotType {}
export const createApiStoreDefaultModel = () => types.optional(APIStoreModel, {})
